import React, { useState, useRef, useEffect } from 'react';
import { TrendingUp, BarChart3, AlertCircle, Check, PlayCircle, UserX, Users, Settings, FileText, Undo2, Menu, X, LogOut } from 'lucide-react';
import EmployeePhoto from './EmployeePhoto';
import { api, resolvePhotoUrl } from '../services/api';
import type { ComparisonViewProps, TabType } from '../types';

const ComparisonView: React.FC<ComparisonViewProps> = ({
  currentComparison,
  activeRound,
  userComparisons,
  totalPossibleComparisons,
  unknownEmployeeCount,
  userRole,
  lastSubmittedComparisonId,
  userName,
  userEmail,
  userPhotoURL,
  onLogout,
  onViewRankings,
  onNavigate,
  onNextPair,
  onComparisonSubmitted,
  onSkip,
  onUndo
}) => {
  const [questionAnswers, setQuestionAnswers] = useState<Record<number, 'left' | 'right'>>({});
  const [unknownSelection, setUnknownSelection] = useState<'left' | 'right' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [undoLoading, setUndoLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detect touch/tablet device — swipe features only activate on coarse pointer devices
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  useEffect(() => {
    setIsTouchDevice(window.matchMedia('(pointer: coarse)').matches);
  }, []);

  // Guard against concurrent submissions (ref is sync, unlike loading state)
  const isSubmittingRef = useRef(false);

  // Always holds the latest currentComparison — avoids stale closures in touch event handlers
  const currentComparisonRef = useRef(currentComparison);
  currentComparisonRef.current = currentComparison;

  // Always holds the latest set of known employee IDs — avoids stale closures in touch handlers
  const knownEmployeeIDsRef = useRef(new Set<number>());

  // Per-card swipe state and refs
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const swipeDirectionRef = useRef<'horizontal' | 'vertical' | null>(null);
  const swipeActionRef = useRef(false);
  const [activeSwipeCard, setActiveSwipeCard] = useState<'left' | 'right' | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [swipeVerticalOffset, setSwipeVerticalOffset] = useState(0);
  const SWIPE_THRESHOLD = 60;

  const isQuestionlessRound = !currentComparison?.questions ||
    currentComparison.questions.length === 0;

  // Separate actual completed comparisons (choice + tie) from skipped/unknown ones
  const actualCompletedComparisons = userComparisons.filter(c => c.comparisonType !== 'skipped' && c.comparisonType !== 'unknown');
  const skippedComparisons = userComparisons.filter(c => c.comparisonType === 'skipped' || c.comparisonType === 'unknown');
  const completedCount = actualCompletedComparisons.length;
  const skippedCount = skippedComparisons.length;

  // If there's a current comparison being shown, ensure at least 1 remaining.
  // Subtract both actual choices AND auto-skipped unknown pairs from the total.
  const remainingComparisons = currentComparison
    ? Math.max(1, totalPossibleComparisons - completedCount - skippedCount)
    : Math.max(0, totalPossibleComparisons - completedCount - skippedCount);

  const actualTotalComparisons = completedCount + remainingComparisons;
  const progressPercentage = actualTotalComparisons > 0
    ? (completedCount / actualTotalComparisons) * 100
    : 0;

  // Build set of employee IDs already involved in actual completed comparisons
  const knownEmployeeIDs = new Set<number>();
  actualCompletedComparisons.forEach(c => {
    knownEmployeeIDs.add(c.employee1ID);
    knownEmployeeIDs.add(c.employee2ID);
  });
  // Keep ref in sync so touch event handlers always see the latest set
  knownEmployeeIDsRef.current = knownEmployeeIDs;

  const employee1Known = currentComparison ? knownEmployeeIDs.has(currentComparison.employee1ID) : false;
  const employee2Known = currentComparison ? knownEmployeeIDs.has(currentComparison.employee2ID) : false;

  const handleQuestionAnswer = (questionId: number, winner: 'left' | 'right') => {
    setQuestionAnswers(prev => ({ ...prev, [questionId]: winner }));
    setError(null);
  };

  const handleUnknownToggle = (side: 'left' | 'right') => {
    setUnknownSelection(prev => prev === side ? null : side);
    if (side !== null) setError(null);
  };

  // Clicking a card immediately submits (questionless rounds)
  const handleDirectSubmit = async (side: 'left' | 'right') => {
    const comp = currentComparisonRef.current;
    if (isSubmittingRef.current || !comp) return;
    isSubmittingRef.current = true;

    setLoading(true);
    setError(null);
    const prevComparisonId = comp.comparisonID;

    try {
      const winnerEmpId = side === 'left' ? comp.employee1ID : comp.employee2ID;
      const loserEmpId = side === 'left' ? comp.employee2ID : comp.employee1ID;

      await api.submitAndComplete({
        comparisonID: comp.comparisonID,
        questionID: 0,
        winnerEmployeeID: winnerEmpId,
        loserEmployeeID: loserEmpId,
        answerType: 'choice'
      });

      onComparisonSubmitted(prevComparisonId);
      await onNextPair();
    } catch (err: any) {
      if (err?.deleted) { await onNextPair(); return; }
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to submit: ${errorMessage}`);
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  // Swipe-up on a card immediately skips that person as unknown
  const handleUnknownSubmit = async (side: 'left' | 'right') => {
    const comp = currentComparisonRef.current;
    if (isSubmittingRef.current || !comp) return;
    isSubmittingRef.current = true;

    setLoading(true);
    setError(null);
    const unknownEmployeeId = side === 'left' ? comp.employee1ID : comp.employee2ID;

    try {
      const skippedId = comp.comparisonID;
      await api.skipComparison(skippedId, 'unknown_employee', unknownEmployeeId);
      onSkip(skippedId);
      await onNextPair();
      setQuestionAnswers({});
      setUnknownSelection(null);
    } catch (err: any) {
      if (err?.deleted) { await onNextPair(); return; }
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to skip: ${errorMessage}`);
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const handleUndo = async () => {
    if (!lastSubmittedComparisonId || undoLoading) return;

    setUndoLoading(true);
    setError(null);

    try {
      await onUndo();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to undo: ${errorMessage}`);
    } finally {
      setUndoLoading(false);
    }
  };

  // ── Swipe handlers (tablet / touch devices) ──────────────────────────────
  const handleCardSwipeStart = (card: 'left' | 'right', e: React.TouchEvent) => {
    if (!isQuestionlessRound || isSubmittingRef.current || !isTouchDevice) return;
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    swipeDirectionRef.current = null;
    setActiveSwipeCard(card);
  };

  const handleCardSwipeMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current || !activeSwipeCard) return;
    const deltaX = e.touches[0].clientX - touchStartRef.current.x;
    const deltaY = e.touches[0].clientY - touchStartRef.current.y;

    if (!swipeDirectionRef.current && (Math.abs(deltaX) > 10 || Math.abs(deltaY) > 10)) {
      swipeDirectionRef.current = Math.abs(deltaX) > Math.abs(deltaY) ? 'horizontal' : 'vertical';
    }

    if (swipeDirectionRef.current === 'horizontal') {
      // Left card only swipes left; right card only swipes right
      if ((activeSwipeCard === 'left' && deltaX < 0) || (activeSwipeCard === 'right' && deltaX > 0)) {
        setSwipeOffset(deltaX);
      }
    } else if (swipeDirectionRef.current === 'vertical' && deltaY < 0) {
      setSwipeVerticalOffset(deltaY);
    }
  };

  const handleCardSwipeEnd = () => {
    if (!touchStartRef.current || !activeSwipeCard) return;

    if (swipeDirectionRef.current === 'horizontal' && Math.abs(swipeOffset) > SWIPE_THRESHOLD) {
      // Left card swiped left → left wins; right card swiped right → right wins
      swipeActionRef.current = true;
      handleDirectSubmit(activeSwipeCard);
      setTimeout(() => { swipeActionRef.current = false; }, 300);
    } else if (swipeDirectionRef.current === 'vertical' && swipeVerticalOffset < -SWIPE_THRESHOLD) {
      // Swipe up → skip as unknown — but only if employee hasn't already been compared
      // (if they were previously picked as winner/loser they are already "known")
      const comp = currentComparisonRef.current;
      const swipedEmployeeId = activeSwipeCard === 'left' ? comp?.employee1ID : comp?.employee2ID;
      const alreadyKnown = swipedEmployeeId != null && knownEmployeeIDsRef.current.has(swipedEmployeeId);

      if (!isSubmittingRef.current && !alreadyKnown) {
        swipeActionRef.current = true;
        handleUnknownSubmit(activeSwipeCard);
        setTimeout(() => { swipeActionRef.current = false; }, 300);
      }
    }

    setSwipeOffset(0);
    setSwipeVerticalOffset(0);
    setActiveSwipeCard(null);
    touchStartRef.current = null;
    swipeDirectionRef.current = null;
  };

  const getCardSwipeStyle = (card: 'left' | 'right'): React.CSSProperties => {
    if (!isTouchDevice || !isQuestionlessRound) return {};
    const base: React.CSSProperties = { touchAction: 'none' };
    if (activeSwipeCard !== card) return { ...base, transition: 'transform 0.3s ease, opacity 0.3s ease' };
    if (swipeOffset !== 0) {
      return {
        ...base,
        transform: `translateX(${swipeOffset * 0.5}px)`,
        opacity: Math.max(0.5, 1 - Math.abs(swipeOffset) / 300),
        transition: 'none'
      };
    }
    if (swipeVerticalOffset !== 0) {
      return {
        ...base,
        transform: `translateY(${swipeVerticalOffset * 0.3}px)`,
        opacity: swipeVerticalOffset < -SWIPE_THRESHOLD ? 0.5 : 1,
        transition: 'none'
      };
    }
    return { ...base, transition: 'transform 0.3s ease, opacity 0.3s ease' };
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleSubmitComparison = async () => {
    if (!currentComparison || isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    setLoading(true);
    setError(null);

    try {
      // Handle unknown employee skip (applies to all round types)
      if (unknownSelection) {
        const unknownEmployeeId = unknownSelection === 'left'
          ? currentComparison.employee1ID
          : currentComparison.employee2ID;

        const skippedId = currentComparison.comparisonID;
        await api.skipComparison(skippedId, 'unknown_employee', unknownEmployeeId);
        onSkip(skippedId);
        await onNextPair();
        setQuestionAnswers({});
        setUnknownSelection(null);
        return;
      }

      // QUESTION-BASED: Submit all answers then complete
      const allAnswered = currentComparison.questions.every(q => questionAnswers[q.questionID]);

      if (!allAnswered) {
        setError('Please answer all questions before submitting');
        setLoading(false);
        return;
      }

      const questions = currentComparison.questions;
      for (let i = 0; i < questions.length - 1; i++) {
        const question = questions[i];
        const winner = questionAnswers[question.questionID];
        const winnerEmpId = winner === 'left' ? currentComparison.employee1ID : currentComparison.employee2ID;
        const loserEmpId = winner === 'left' ? currentComparison.employee2ID : currentComparison.employee1ID;

        await api.recordAnswer({
          comparisonID: currentComparison.comparisonID,
          questionID: question.questionID,
          winnerEmployeeID: winnerEmpId,
          loserEmployeeID: loserEmpId,
          answerType: 'choice'
        });
      }

      const lastQuestion = questions[questions.length - 1];
      const lastWinner = questionAnswers[lastQuestion.questionID];
      const lastWinnerEmpId = lastWinner === 'left' ? currentComparison.employee1ID : currentComparison.employee2ID;
      const lastLoserEmpId = lastWinner === 'left' ? currentComparison.employee2ID : currentComparison.employee1ID;

      await api.submitAndComplete({
        comparisonID: currentComparison.comparisonID,
        questionID: lastQuestion.questionID,
        winnerEmployeeID: lastWinnerEmpId,
        loserEmployeeID: lastLoserEmpId,
        answerType: 'choice'
      });

      await onNextPair();
      setQuestionAnswers({});
      setUnknownSelection(null);
    } catch (err: any) {
      if (err?.deleted) { await onNextPair(); return; }
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to submit comparison: ${errorMessage}`);
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const allAnswered = currentComparison?.questions.every(q => questionAnswers[q.questionID]);
  const canSubmit = (!isQuestionlessRound && allAnswered) || !!unknownSelection;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-2 sm:p-3">
      {/* Header + Navigation: consistent with dashboard */}
      {(() => {
        const navTabs = [
          { id: 'overview' as TabType, label: 'Overview', icon: BarChart3 },
          { id: 'rounds' as TabType, label: 'Evaluation Rounds', icon: PlayCircle },
          { id: 'rankings' as TabType, label: 'Rankings', icon: TrendingUp },
          { id: 'skipped' as TabType, label: 'Skipped', icon: UserX },
          ...(userRole === 'admin' ? [
            { id: 'employees' as TabType, label: 'Employees', icon: Users },
            { id: 'settings' as TabType, label: 'Settings', icon: Settings },
            { id: 'auditlog' as TabType, label: 'Audit Log', icon: FileText },
          ] : []),
        ] as { id: TabType; label: string; icon: React.ComponentType<{ className?: string }> }[];

        const userInitials = userName
          ? userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
          : '?';
        const resolvedPhoto = resolvePhotoUrl(userPhotoURL);

        const profileBlock = (
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <div className="text-right hidden md:block">
              <p className="text-sm font-semibold text-gray-900 truncate max-w-[150px]">{userName}</p>
              <p className="text-xs text-gray-500 truncate max-w-[150px]">{userEmail}</p>
            </div>
            {resolvedPhoto ? (
              <img
                src={resolvedPhoto}
                alt={userName}
                className="w-8 h-8 md:w-10 md:h-10 rounded-full object-cover shadow flex-shrink-0"
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; (e.target as HTMLImageElement).nextElementSibling?.removeAttribute('style'); }}
              />
            ) : null}
            <div
              className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center text-white font-bold text-xs md:text-sm shadow flex-shrink-0"
              style={resolvedPhoto ? { display: 'none' } : undefined}
            >
              {userInitials}
            </div>
            <button
              onClick={onLogout}
              className="flex items-center gap-1 px-2 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors min-h-[44px] min-w-[44px] justify-center"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">Sign Out</span>
            </button>
          </div>
        );

        return (
          <div className="max-w-7xl mx-auto mb-2 space-y-2">
            {/* Nav bar: hamburger on mobile, tabs + profile on desktop */}
            <div className="bg-white rounded-xl shadow-md px-2 sm:px-4 py-2 border border-gray-100">
              {/* Mobile: hamburger + label | profile + logout */}
              <div className="md:hidden flex items-center justify-between">
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-100 transition-colors min-h-[44px]"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5 text-gray-600" /> : <Menu className="w-5 h-5 text-gray-600" />}
                  <BarChart3 className="w-4 h-4 text-blue-600" />
                  <span className="font-semibold text-sm text-gray-900">Comparison</span>
                </button>
                {profileBlock}
              </div>
              {/* Mobile: dropdown menu */}
              {mobileMenuOpen && (
                <div className="md:hidden pb-2 border-t border-gray-100 mt-1">
                  <nav className="flex flex-col gap-1 pt-2">
                    {navTabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => { onNavigate(tab.id); setMobileMenuOpen(false); }}
                        className="flex items-center gap-3 px-3 py-3 rounded-lg transition-all min-h-[44px] text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                      >
                        <tab.icon className="w-5 h-5" />
                        <span className="font-medium text-sm">{tab.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
              )}
              {/* Desktop: tabs + profile in one row */}
              <div className="hidden md:flex items-center justify-between">
                <div className="flex items-center gap-0.5">
                  {navTabs.map(tab => (
                    <button
                      key={tab.id}
                      onClick={() => onNavigate(tab.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium cursor-pointer whitespace-nowrap flex-shrink-0 min-h-[44px]"
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
                {profileBlock}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Responsive: header padding and layout adapt to screen size */}
      <div className="max-w-7xl mx-auto mb-2">
        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-4 border border-gray-100">
          <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 mb-2">
            <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Pairwise Comparison
            </h1>
            <div className="flex items-center gap-2 flex-shrink-0">
              {isQuestionlessRound && lastSubmittedComparisonId !== null && (
                <button
                  onClick={handleUndo}
                  disabled={undoLoading}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200 min-h-[44px]"
                >
                  {undoLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  ) : (
                    <Undo2 className="w-4 h-4" />
                  )}
                  Undo
                </button>
              )}
              <button
                onClick={onViewRankings}
                className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all duration-150 shadow-md hover:shadow-lg text-sm font-medium cursor-pointer min-h-[44px]"
              >
                <TrendingUp className="w-4 h-4" />
                View Rankings
              </button>
            </div>
          </div>

          <div className="mb-1">
            <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
              <div className="font-medium">
                <span>{completedCount} completed</span>
                {unknownEmployeeCount > 0 && (
                  <span className="text-orange-600 ml-2">({unknownEmployeeCount} skipped)</span>
                )}
              </div>
              <span className="font-medium">~{remainingComparisons} remaining</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 shadow-inner">
              <div
                className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all duration-500 shadow-sm"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {activeRound && (
            <p className="text-xs text-gray-600 mt-2 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              Round: <strong className="text-gray-900 truncate max-w-xs inline-block align-bottom" title={activeRound.roundName}>{activeRound.roundName}</strong>
            </p>
          )}
        </div>
      </div>

      {/* Responsive: employee cards stack on mobile, side-by-side on tablet+ */}
      <div className="max-w-7xl mx-auto mb-2">
        {/* Always side-by-side: comparison cards must stay left/right even on mobile */}
        <div className="grid grid-cols-2 gap-2 sm:gap-4">
          {/* Left Employee */}
          <div
            onClick={isQuestionlessRound && !unknownSelection && !loading && !isTouchDevice ? () => {
              if (swipeActionRef.current) return;
              handleDirectSubmit('left');
            } : undefined}
            onTouchStart={(e) => handleCardSwipeStart('left', e)}
            onTouchMove={handleCardSwipeMove}
            onTouchEnd={handleCardSwipeEnd}
            style={getCardSwipeStyle('left')}
            className={`bg-white rounded-xl sm:rounded-2xl shadow-2xl p-2 sm:p-5 transition-all border-2 flex flex-col ${
              unknownSelection === 'left'
                ? 'opacity-50 border-orange-300'
                : isQuestionlessRound && !unknownSelection
                  ? loading
                    ? 'border-transparent opacity-60'
                    : isTouchDevice
                      ? 'border-transparent'
                      : 'border-transparent hover:shadow-3xl hover:border-blue-300 cursor-pointer'
                  : 'border-transparent hover:shadow-3xl'
            }`}>
            <div className="text-center flex flex-col flex-1">
              {/* Side-by-side: smaller photo on mobile to fit both cards */}
              <div className="flex justify-center mb-1 sm:mb-3">
                <EmployeePhoto
                  photoURL={currentComparison?.employee1Photo}
                  name={currentComparison?.employee1Name}
                  size="large"
                />
              </div>
              <h3 className="text-sm sm:text-xl font-bold text-gray-900 mb-0.5 sm:mb-1 truncate">
                {currentComparison?.employee1Name}
              </h3>
              {currentComparison?.employee1Title && (
                <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1 font-medium truncate">
                  {currentComparison.employee1Title}
                </p>
              )}
              {currentComparison?.employee1Department && (
                <p className="text-xs sm:text-sm text-gray-500 mb-0.5 sm:mb-1 truncate">
                  {currentComparison.employee1Department}
                </p>
              )}
              {currentComparison?.employee1Manager && (
                <p className="text-sm text-gray-600 mb-1">
                  {currentComparison.employee1Manager}
                </p>
              )}

              <div className="mt-auto">
                {!employee1Known && !(isTouchDevice && isQuestionlessRound) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      isQuestionlessRound ? handleUnknownSubmit('left') : handleUnknownToggle('left');
                    }}
                    disabled={loading}
                    className={`mt-2 sm:mt-3 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 shadow-md hover:shadow-lg active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] ${
                      !isQuestionlessRound && unknownSelection === 'left'
                        ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white'
                        : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
                    }`}
                  >
                    {!isQuestionlessRound && unknownSelection === 'left' ? (
                      <><Check className="w-4 h-4 inline mr-1" /><span className="hidden sm:inline">Marked as </span>Unknown</>
                    ) : (
                      <><span className="hidden sm:inline">I Don't Know This Person</span><span className="sm:hidden">Don't Know</span></>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Employee */}
          <div
            onClick={isQuestionlessRound && !unknownSelection && !loading && !isTouchDevice ? () => {
              if (swipeActionRef.current) return;
              handleDirectSubmit('right');
            } : undefined}
            onTouchStart={(e) => handleCardSwipeStart('right', e)}
            onTouchMove={handleCardSwipeMove}
            onTouchEnd={handleCardSwipeEnd}
            style={getCardSwipeStyle('right')}
            className={`bg-white rounded-xl sm:rounded-2xl shadow-2xl p-2 sm:p-5 transition-all border-2 flex flex-col ${
              unknownSelection === 'right'
                ? 'opacity-50 border-orange-300'
                : isQuestionlessRound && !unknownSelection
                  ? loading
                    ? 'border-transparent opacity-60'
                    : isTouchDevice
                      ? 'border-transparent'
                      : 'border-transparent hover:shadow-3xl hover:border-green-300 cursor-pointer'
                  : 'border-transparent hover:shadow-3xl'
            }`}>
            <div className="text-center flex flex-col flex-1">
              {/* Side-by-side: smaller photo on mobile to fit both cards */}
              <div className="flex justify-center mb-1 sm:mb-3">
                <EmployeePhoto
                  photoURL={currentComparison?.employee2Photo}
                  name={currentComparison?.employee2Name}
                  size="large"
                />
              </div>
              <h3 className="text-sm sm:text-xl font-bold text-gray-900 mb-0.5 sm:mb-1 truncate">
                {currentComparison?.employee2Name}
              </h3>
              {currentComparison?.employee2Title && (
                <p className="text-xs sm:text-sm text-gray-600 mb-0.5 sm:mb-1 font-medium truncate">
                  {currentComparison.employee2Title}
                </p>
              )}
              {currentComparison?.employee2Department && (
                <p className="text-xs sm:text-sm text-gray-500 mb-0.5 sm:mb-1 truncate">
                  {currentComparison.employee2Department}
                </p>
              )}
              {currentComparison?.employee2Manager && (
                <p className="text-sm text-gray-600 mb-1">
                  {currentComparison.employee2Manager}
                </p>
              )}

              <div className="mt-auto">
                {!employee2Known && !(isTouchDevice && isQuestionlessRound) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      isQuestionlessRound ? handleUnknownSubmit('right') : handleUnknownToggle('right');
                    }}
                    disabled={loading}
                    className={`mt-2 sm:mt-3 px-2 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-all duration-150 shadow-md hover:shadow-lg active:scale-95 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] ${
                      !isQuestionlessRound && unknownSelection === 'right'
                        ? 'bg-gradient-to-r from-orange-600 to-orange-700 text-white'
                        : 'bg-orange-50 text-orange-700 hover:bg-orange-100 border border-orange-200'
                    }`}
                  >
                    {!isQuestionlessRound && unknownSelection === 'right' ? (
                      <><Check className="w-4 h-4 inline mr-1" /><span className="hidden sm:inline">Marked as </span>Unknown</>
                    ) : (
                      <><span className="hidden sm:inline">I Don't Know This Person</span><span className="sm:hidden">Don't Know</span></>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Questions Section - Only show if round has questions */}
      {!unknownSelection && !isQuestionlessRound && (
        <div className="max-w-7xl mx-auto mb-2">
          {/* Responsive: question section padding scales */}
          <div className="bg-white rounded-xl shadow-2xl p-3 sm:p-5 border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">
              Compare by answering these questions
            </h2>

            <div className="space-y-4">
              {currentComparison?.questions.map((question, index) => (
                <div key={question.questionID} className="border-b border-gray-200 pb-4 last:border-b-0">
                  <div className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center font-bold text-xs shadow-md">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-base font-semibold text-gray-900 mb-3">{question.questionText}</p>

                      {/* Responsive: answer buttons stack on small mobile, side-by-side on wider screens */}
                      <div className="flex flex-col xs:flex-row gap-2 sm:gap-3 items-stretch xs:items-center">
                        <button
                          onClick={() => handleQuestionAnswer(question.questionID, 'left')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-lg border-2 transition-all duration-150 shadow-sm hover:shadow-md active:scale-98 cursor-pointer min-h-[44px] ${
                            questionAnswers[question.questionID] === 'left'
                              ? 'border-blue-600 bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            questionAnswers[question.questionID] === 'left'
                              ? 'border-blue-600 bg-blue-600'
                              : 'border-gray-300'
                          }`}>
                            {questionAnswers[question.questionID] === 'left' && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <span className="font-medium text-sm text-gray-900">
                            {currentComparison.employee1Name}
                          </span>
                        </button>

                        <button
                          onClick={() => handleQuestionAnswer(question.questionID, 'right')}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-3 rounded-lg border-2 transition-all duration-150 shadow-sm hover:shadow-md active:scale-98 cursor-pointer min-h-[44px] ${
                            questionAnswers[question.questionID] === 'right'
                              ? 'border-green-600 bg-green-50 shadow-md'
                              : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                            questionAnswers[question.questionID] === 'right'
                              ? 'border-green-600 bg-green-600'
                              : 'border-gray-300'
                          }`}>
                            {questionAnswers[question.questionID] === 'right' && (
                              <Check className="w-3 h-3 text-white" />
                            )}
                          </div>
                          <span className="font-medium text-sm text-gray-900">
                            {currentComparison.employee2Name}
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Bottom Action Section */}
      <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-xl shadow-2xl p-4 border border-gray-100">
          {error && (
            <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm shadow-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{error}</span>
            </div>
          )}

          {/* Questionless round + no unknown: hint text only (undo is in header) */}
          {isQuestionlessRound && !unknownSelection ? (
            <div className="flex flex-col items-center gap-3">
              {loading ? (
                <span className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  Submitting...
                </span>
              ) : (
                <p className="text-center text-sm text-gray-500">
                  {isTouchDevice
                    ? 'Swipe card left/right to select winner · swipe up to skip'
                    : 'Click an employee card above to select the winner'}
                </p>
              )}
            </div>
          ) : (
            /* Unknown skip OR question-based round: submit button */
            <>
              <button
                onClick={handleSubmitComparison}
                disabled={!canSubmit || loading}
                className={`w-full px-4 sm:px-6 py-3 rounded-lg font-bold text-base transition-all duration-150 min-h-[44px] ${
                  canSubmit && !loading
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl active:scale-98 cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Submitting...
                  </span>
                ) : unknownSelection ? (
                  'Submit & Skip to Next Pair'
                ) : allAnswered ? (
                  'Submit All Answers'
                ) : (
                  `Answer ${currentComparison?.questions.length - Object.keys(questionAnswers).length} more question(s)`
                )}
              </button>

              {!unknownSelection && (
                <p className="text-center text-xs text-gray-600 mt-2">
                  {allAnswered
                    ? 'All questions answered! Click submit to continue.'
                    : 'Please answer all questions before submitting'}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ComparisonView;
